// Test tooling: bulk-create dummy app users on a booking so the roll-call / QR
// work can be exercised at group scale (iGo has never run a group past ~40).
//
// These write plain Firestore documents and deliberately DO NOT call the
// registerClientUser cloud function, so no Firebase Auth accounts are created.
// That is what makes running this against production safe: nothing under
// bookings/{id}/* has a Firestore trigger, and the only triggers in the codebase
// are on email/{id} onCreate and users/{id} onDelete.
//
// Every seeded document carries seeded: true so removeSeededUsers can delete
// exactly what was added and nothing else.

import cuid from "cuid";
import firebase from "../../../app/config/firebase";

// Firestore allows 500 operations per batch; stay under it.
const BATCH_SIZE = 400;

// Deliberately varied lengths — name length drives the QR label layout, so a
// roster of uniformly short names would not surface print problems.
const FIRST_NAMES = [
    "Thabo", "Lerato", "Sipho", "Naledi", "Johan", "Anelisa", "Pieter", "Zanele",
    "Rajesh", "Ayanda", "Michelle", "Sizwe", "Bronwyn", "Kagiso", "Christiaan",
    "Nomvula", "Deon", "Fatima", "Tshepo", "Charmaine", "Bongani", "Elandri",
    "Yusuf", "Palesa", "Hendrik", "Refilwe", "Shaun", "Nokuthula", "Werner", "Asanda",
];

const LAST_NAMES = [
    "Mokoena", "Van der Merwe", "Dlamini", "Naidoo", "Botha", "Nkosi", "Pillay",
    "Khumalo", "Oosthuizen", "Mahlangu", "Jacobs", "Sithole", "Van Wyk", "Maseko",
    "Ferreira", "Ndlovu", "Coetzee", "Mthembu", "Swanepoel", "Zulu", "Adams",
    "Molefe", "Labuschagne", "Ngcobo", "Fourie", "Radebe", "Steenkamp", "Mabaso",
];

function pick(list, i, salt) {
    return list[(i * 7 + salt) % list.length];
}

/**
 * Add `count` dummy app users to bookings/{bookingId}/users.
 *
 * @param {string} bookingId
 * @param {string} leadId     stored on each doc to match the real Add User flow
 * @param {number} count
 * @param {function} [onProgress] called with (written, total) after each batch
 * @returns {Promise<{created: number, seedBatch: string}>}
 */
export async function seedAppUsers(bookingId, leadId, count, onProgress) {
    if (!bookingId) throw new Error("seedAppUsers: bookingId is required");

    const total = Number(count);
    if (!Number.isInteger(total) || total < 1 || total > 1000) {
        throw new Error("seedAppUsers: count must be a whole number between 1 and 1000");
    }

    const firestore = firebase.firestore();
    const usersRef = firestore.collection("bookings").doc(bookingId).collection("users");

    // Tags this run so it can be removed independently of any earlier run.
    const seedBatch = cuid();
    let written = 0;

    while (written < total) {
        const batch = firestore.batch();
        const upTo = Math.min(written + BATCH_SIZE, total);

        for (let i = written; i < upTo; i++) {
            const n = i + 1;
            const name = `${pick(FIRST_NAMES, i, 3)} ${pick(LAST_NAMES, i, 11)}`;

            batch.set(usersRef.doc(), {
                client_name: name,
                // .invalid can never route real mail (RFC 2606), so a stray
                // notification cannot reach a real person.
                client_email: `rollcall-seed-${n}@example.invalid`,
                password: "seed-not-a-real-login",
                userType: n === 1 ? "Group Admin" : "User",
                leadId: leadId || null,
                bookingId: bookingId,
                seeded: true,
                seedBatch: seedBatch,
                seededAt: new Date(Date.now()),
            });
        }

        await batch.commit();
        written = upTo;
        if (onProgress) onProgress(written, total);
    }

    return { created: total, seedBatch };
}

/**
 * Delete seeded app users from a booking. Only ever touches documents written by
 * seedAppUsers — real app users have no `seeded` field and are never matched.
 *
 * @param {string} bookingId
 * @param {string} [seedBatch] restrict to one run; omit to remove all seeded users
 * @returns {Promise<number>} number of documents deleted
 */
export async function removeSeededUsers(bookingId, seedBatch) {
    if (!bookingId) throw new Error("removeSeededUsers: bookingId is required");

    const firestore = firebase.firestore();
    let query = firestore.collection("bookings").doc(bookingId).collection("users").where("seeded", "==", true);

    if (seedBatch) query = query.where("seedBatch", "==", seedBatch);

    const snapshot = await query.get();
    if (snapshot.empty) return 0;

    const docs = snapshot.docs;
    let removed = 0;

    while (removed < docs.length) {
        const batch = firestore.batch();
        const upTo = Math.min(removed + BATCH_SIZE, docs.length);

        for (let i = removed; i < upTo; i++) {
            batch.delete(docs[i].ref);
        }

        await batch.commit();
        removed = upTo;
    }

    return removed;
}
